import { useCallback, useEffect, useState } from 'react';

import { PersonalAccountAddressInfoType, PersonalAccountInfoType } from '@screens/PersonalAccount';
import { InputProps } from '@shared/ui/Input';
import { isNumber, validateEmail, validatePostalCode } from '@shared/utils/fieldValidators/fieldValidators';

type UsePersonalAccountFieldsProps = {
  profileInfo?: PersonalAccountInfoType;
  addressInfo?: PersonalAccountAddressInfoType;
};

export const usePersonalAccountFields = (props: UsePersonalAccountFieldsProps) => {
  const {
    profileInfo: {
      account: { email: emailProps, phone: phoneNumberProps } = {},
      firstName: firstNameProps,
      secondName: secondNameProps,
      lastName: lastNameProps
    } = {},
    addressInfo: {
      postalCode: postalCodeProps,
      region: regionProps,
      city: cityProps,
      street: streetProps,
      house: houseNumberProps,
      corpus: corpusProps,
      building: buildingProps,
      apartment: apartmentProps,
      floor: floorProps
    } = {}
  } = props;

  const [email, setEmail] = useState(emailProps || undefined);
  const [secondName, setSecondName] = useState(secondNameProps || undefined);
  const [firstName, setFirstName] = useState(firstNameProps || undefined);
  const [lastName, setLastName] = useState(lastNameProps || undefined);
  const [phoneNumber, setPhoneNumber] = useState(phoneNumberProps || undefined);

  const [city, setCity] = useState(cityProps || undefined);
  const [street, setStreet] = useState(streetProps || undefined);
  const [houseNumber, setHouseNumber] = useState(houseNumberProps || undefined);
  const [region, setRegion] = useState(regionProps || undefined);
  const [corpus, setCorpus] = useState(corpusProps || undefined);
  const [building, setBuilding] = useState(buildingProps || undefined);
  const [apartment, setApartment] = useState(apartmentProps || undefined);
  const [postalCode, setPostalCode] = useState(postalCodeProps || undefined);
  const [floor, setFloor] = useState(floorProps || undefined);
  const [comment, setComment] = useState('');

  const [errorFirstName, setErrorFirstName] = useState(false);
  const [errorLastName, setErrorLastName] = useState(false);
  const [errorEmail, setErrorEmail] = useState(false);
  const [errorPostalCode, setErrorPostalCode] = useState(false);

  const [errorRegion, setErrorRegion] = useState(false);
  const [errorCity, setErrorCity] = useState(false);
  const [errorStreet, setErrorStreet] = useState(false);
  const [errorHouseNumber, setErrorHouseNumber] = useState(false);

  const disabledButton = !(firstName && secondName && validateEmail(email));

  const handleChangeEmail = useCallback<NonNullable<InputProps['onChange']>>((value) => {
    setEmail(value);

    if (validateEmail(value)) {
      setErrorEmail(false);
    }
  }, []);

  const handleChangeFirstName = useCallback<NonNullable<InputProps['onChange']>>((value) => {
    setFirstName(value);
    setErrorFirstName(false);
  }, []);

  const handleChangeSecondName = useCallback<NonNullable<InputProps['onChange']>>((value) => {
    setSecondName(value);
  }, []);

  const handleChangeLastName = useCallback<NonNullable<InputProps['onChange']>>((value) => {
    setLastName(value);
    setErrorLastName(false);
  }, []);

  const handleChangePhoneNumber = useCallback<NonNullable<InputProps['onChange']>>((value) => {
    setPhoneNumber(value);
  }, []);

  const handleChangeRegion = useCallback<NonNullable<InputProps['onChange']>>((value) => {
    setRegion(value);
    setErrorRegion(false);
  }, []);

  const handleChangeCity = useCallback<NonNullable<InputProps['onChange']>>((value) => {
    setCity(value);
    setErrorCity(false);
  }, []);

  const handleChangeStreet = useCallback<NonNullable<InputProps['onChange']>>((value) => {
    setStreet(value);
    setErrorStreet(false);
  }, []);

  const handleChangeHouseNumber = useCallback<NonNullable<InputProps['onChange']>>((value) => {
    setHouseNumber(value);
    setErrorHouseNumber(false);
  }, []);

  const handleChangeCorpus = useCallback<NonNullable<InputProps['onChange']>>((value) => {
    if (isNumber(value)) {
      setCorpus(value);
    } else if (!value) {
      setCorpus(undefined);
    }
  }, []);

  const handleChangeBuilding = useCallback<NonNullable<InputProps['onChange']>>((value) => {
    if (isNumber(value)) {
      setBuilding(value);
    } else if (!value) {
      setBuilding(undefined);
    }
  }, []);

  const handleChangeApartment = useCallback<NonNullable<InputProps['onChange']>>((value) => {
    if (isNumber(value)) {
      setApartment(value);
    } else if (!value) {
      setApartment(undefined);
    }
  }, []);

  const handleChangeFloor = useCallback<NonNullable<InputProps['onChange']>>((value) => {
    if (isNumber(value)) {
      setFloor(value);
    } else if (!value) {
      setFloor(undefined);
    }
  }, []);

  const handleChangeComment = useCallback<NonNullable<InputProps['onChange']>>((value) => {
    setComment(value);
  }, []);

  const handleChangePostalCode = useCallback<NonNullable<InputProps['onChange']>>((value) => {
    if (value) {
      setPostalCode(value);
    } else {
      setPostalCode(undefined);
    }

    if (validatePostalCode(value)) {
      setErrorPostalCode(false);
    }
  }, []);

  useEffect(() => {
    setEmail(emailProps || undefined);
    setSecondName(secondNameProps || undefined);
    setFirstName(firstNameProps || undefined);
    setLastName(lastNameProps || undefined);
    setPhoneNumber(phoneNumberProps || undefined);
    setCity(cityProps || undefined);
    setStreet(streetProps || undefined);
    setHouseNumber(houseNumberProps || undefined);
    setRegion(regionProps || undefined);
    setCorpus(corpusProps || undefined);
    setBuilding(buildingProps || undefined);
    setApartment(apartmentProps || undefined);
    setPostalCode(postalCodeProps || undefined);
    setFloor(floorProps || undefined);
  }, [
    emailProps,
    secondNameProps,
    firstNameProps,
    lastNameProps,
    phoneNumberProps,
    cityProps,
    streetProps,
    houseNumberProps,
    regionProps,
    corpusProps,
    buildingProps,
    apartmentProps,
    postalCodeProps,
    floorProps
  ]);

  return {
    email,
    disabledButton,
    lastName,
    firstName,
    secondName,
    phoneNumber,
    region,
    city,
    street,
    houseNumber,
    corpus,
    building,
    apartment,
    floor,
    postalCode,
    comment,
    errorFirstName,
    errorLastName,
    errorPostalCode,
    errorEmail,
    errorRegion,
    errorCity,
    errorStreet,
    errorHouseNumber,
    handleChangeEmail,
    handleChangeLastName,
    handleChangeFirstName,
    handleChangeSecondName,
    handleChangePhoneNumber,
    handleChangeRegion,
    handleChangeCity,
    handleChangeStreet,
    handleChangeHouseNumber,
    handleChangeCorpus,
    handleChangeBuilding,
    handleChangeApartment,
    handleChangeFloor,
    handleChangeComment,
    handleChangePostalCode,
    setErrorFirstName,
    setErrorLastName,
    setErrorEmail,
    setErrorPostalCode,
    setErrorRegion,
    setErrorCity,
    setErrorStreet,
    setErrorHouseNumber
  };
};
